import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Search,
  X,
  Clock,
  TrendingUp,
  BookOpen,
  Users,
  FileText,
  Award,
  Folder,
  BarChart,
  MessageSquare,
  Layers,
  Command,
  ArrowRight,
  Hash
} from 'lucide-react';
import { useGlobalSearchStore } from '@/stores/GlobalSearchStore';
import { SearchResult, SearchCategory } from '@/services/GlobalSearchService';
import './GlobalSearch.css';

interface GlobalSearchProps {
  isOpen: boolean;
  onClose: () => void;
}

const CATEGORY_ICONS: Record<SearchCategory, React.ReactElement> = {
  courses: <BookOpen size={16} />,
  users: <Users size={16} />,
  content: <FileText size={16} />,
  assessments: <FileText size={16} />,
  projects: <Folder size={16} />,
  certifications: <Award size={16} />,
  feedback: <MessageSquare size={16} />,
  ilt: <Layers size={16} />,
  reports: <BarChart size={16} />,
  pages: <Hash size={16} />,
};

const CATEGORY_LABELS: Record<SearchCategory, string> = {
  courses: 'Courses',
  users: 'Users',
  content: 'Content',
  assessments: 'Assessments',
  projects: 'Projects',
  certifications: 'Certifications',
  feedback: 'Feedback',
  ilt: 'ILT Sessions',
  reports: 'Reports',
  pages: 'Pages',
};

const GlobalSearch: React.FC<GlobalSearchProps> = ({ isOpen, onClose }) => {
  const navigate = useNavigate();
  const inputRef = useRef<HTMLInputElement>(null);
  const resultsRef = useRef<HTMLDivElement>(null);

  const {
    query,
    results,
    recentSearches,
    trending,
    loading,
    setQuery,
    search,
    addRecentSearch,
    clearRecentSearches,
  } = useGlobalSearchStore();

  const [selectedIndex, setSelectedIndex] = useState(0);
  const [activeCategory, setActiveCategory] = useState<SearchCategory | 'all'>('all');

  // Focus input when modal opens
  useEffect(() => {
    if (isOpen) {
      setTimeout(() => inputRef.current?.focus(), 100);
      setSelectedIndex(0);
    }
  }, [isOpen]);

  // Search when query changes
  useEffect(() => {
    if (query.length >= 2) {
      search();
    }
  }, [query, search]);

  // Filter results by category
  const filteredResults = useMemo(() => {
    if (activeCategory === 'all') return results;
    return results.filter(r => r.category === activeCategory);
  }, [results, activeCategory]);

  // Group results by category
  const groupedResults = useMemo(() => {
    const groups: Record<string, SearchResult[]> = {};
    filteredResults.forEach(result => {
      if (!groups[result.category]) {
        groups[result.category] = [];
      }
      groups[result.category].push(result);
    });
    return groups;
  }, [filteredResults]);

  // All result items in flat array for keyboard navigation
  const allResultItems = useMemo(() => filteredResults, [filteredResults]);

  const handleResultClick = (result: SearchResult) => {
    addRecentSearch(result);
    navigate(result.url);
    onClose();
    setQuery('');
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      onClose();
      setQuery('');
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedIndex(prev => (prev + 1) % Math.max(allResultItems.length, 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedIndex(prev => (prev - 1 + allResultItems.length) % Math.max(allResultItems.length, 1));
    } else if (e.key === 'Enter' && allResultItems[selectedIndex]) {
      e.preventDefault();
      handleResultClick(allResultItems[selectedIndex]);
    }
  };

  // Scroll selected item into view
  useEffect(() => {
    if (resultsRef.current) {
      const selected = resultsRef.current.querySelector('.search-result-item.selected');
      selected?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    }
  }, [selectedIndex]);

  const renderEmptyState = () => {
    if (query.length < 2) {
      return (
        <div className="search-empty-state">
          {recentSearches.length > 0 && (
            <div className="search-section">
              <div className="search-section-header">
                <div className="search-section-title">
                  <Clock size={16} />
                  Recent Searches
                </div>
                <button
                  className="search-clear-btn"
                  onClick={clearRecentSearches}
                >
                  Clear
                </button>
              </div>
              <div className="search-recent-list">
                {recentSearches.map((item, idx) => (
                  <div
                    key={idx}
                    className="search-recent-item"
                    onClick={() => handleResultClick(item)}
                  >
                    {CATEGORY_ICONS[item.category]}
                    <span>{item.title}</span>
                    <ArrowRight size={14} className="search-recent-arrow" />
                  </div>
                ))}
              </div>
            </div>
          )}

          {trending.length > 0 && (
            <div className="search-section">
              <div className="search-section-header">
                <div className="search-section-title">
                  <TrendingUp size={16} />
                  Trending
                </div>
              </div>
              <div className="search-trending-list">
                {trending.map((item, idx) => (
                  <div
                    key={idx}
                    className="search-trending-item"
                    onClick={() => handleResultClick(item)}
                  >
                    {CATEGORY_ICONS[item.category]}
                    <span>{item.title}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {recentSearches.length === 0 && trending.length === 0 && (
            <div className="search-no-recent">
              <Search size={48} />
              <p>Start typing to search</p>
              <span>Search for courses, users, content, and more</span>
            </div>
          )}
        </div>
      );
    }

    if (loading) {
      return (
        <div className="search-loading">
          <div className="search-loading-spinner" />
          <span>Searching...</span>
        </div>
      );
    }

    if (query.length >= 2 && results.length === 0) {
      return (
        <div className="search-no-results">
          <Search size={48} />
          <p>No results found for "{query}"</p>
          <span>Try different keywords or check your spelling</span>
        </div>
      );
    }

    return null;
  };

  const renderResults = () => {
    if (query.length < 2 || loading || results.length === 0) {
      return renderEmptyState();
    }

    let itemIndex = 0;

    return (
      <div className="search-results-container" ref={resultsRef}>
        {Object.entries(groupedResults).map(([category, items]) => (
          <div key={category} className="search-results-group">
            <div className="search-results-group-header">
              {CATEGORY_ICONS[category as SearchCategory]}
              <span>{CATEGORY_LABELS[category as SearchCategory]}</span>
              <span className="search-results-count">{items.length}</span>
            </div>
            {items.map((result) => {
              const currentIndex = itemIndex++;
              return (
                <div
                  key={result.id}
                  className={`search-result-item ${selectedIndex === currentIndex ? 'selected' : ''}`}
                  onClick={() => handleResultClick(result)}
                  onMouseEnter={() => setSelectedIndex(currentIndex)}
                >
                  <div className="search-result-icon">
                    {CATEGORY_ICONS[result.category]}
                  </div>
                  <div className="search-result-content">
                    <div className="search-result-title">{result.title}</div>
                    {result.description && (
                      <div className="search-result-description">{result.description}</div>
                    )}
                    {result.metadata && (
                      <div className="search-result-metadata">
                        {Object.entries(result.metadata).map(([key, value]) => (
                          <span key={key} className="search-result-meta-item">
                            {value}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                  <ArrowRight size={16} className="search-result-arrow" />
                </div>
              );
            })}
          </div>
        ))}
      </div>
    );
  };

  // Get unique categories from results
  const availableCategories = useMemo(() => {
    const categories = new Set(results.map(r => r.category));
    return Array.from(categories);
  }, [results]);

  if (!isOpen) return null;

  return (
    <div className="global-search-overlay" onClick={onClose}>
      <div className="global-search-modal" onClick={(e) => e.stopPropagation()} onKeyDown={handleKeyDown}>
        <div className="global-search-header">
          <div className="global-search-input-wrapper">
            <Search size={20} className="global-search-icon" />
            <input
              ref={inputRef}
              type="text"
              className="global-search-input"
              placeholder="Search courses, users, content..."
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              autoComplete="off"
            />
            {query && (
              <button
                className="global-search-clear"
                onClick={() => setQuery('')}
                aria-label="Clear search"
              >
                <X size={16} />
              </button>
            )}
          </div>
          <button
            className="global-search-close-btn"
            onClick={onClose}
            aria-label="Close search"
            title="Close search (Esc)"
          >
            <X size={20} />
          </button>

          {results.length > 0 && availableCategories.length > 1 && (
            <div className="global-search-filters">
              <button
                className={`search-filter-btn ${activeCategory === 'all' ? 'active' : ''}`}
                onClick={() => setActiveCategory('all')}
              >
                All ({results.length})
              </button>
              {availableCategories.map((cat) => {
                const count = results.filter(r => r.category === cat).length;
                return (
                  <button
                    key={cat}
                    className={`search-filter-btn ${activeCategory === cat ? 'active' : ''}`}
                    onClick={() => setActiveCategory(cat)}
                  >
                    {CATEGORY_ICONS[cat]}
                    {CATEGORY_LABELS[cat]} ({count})
                  </button>
                );
              })}
            </div>
          )}
        </div>

        <div className="global-search-body">
          {renderResults()}
        </div>

        <div className="global-search-footer">
          <div className="search-footer-shortcuts">
            <div className="search-shortcut">
              <kbd>↑↓</kbd> Navigate
            </div>
            <div className="search-shortcut">
              <kbd>↵</kbd> Select
            </div>
            <div className="search-shortcut">
              <kbd>Esc</kbd> Close
            </div>
          </div>
          <div className="search-footer-hint">
            <Command size={12} />
            <span>Tip: Press Cmd/Ctrl + K to open search</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GlobalSearch;
